import { LitElement, html, css, nothing, type PropertyValues, type TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { authorizedRequest } from "./wire";
import { freshCredential, type Session } from "./sign-in";
import { cursors, palette } from "./theme";

// The admin tab (shown only to ADMIN_USER_IDS): players' bug reports from GitHub and every account
// that has signed in, as two lists beside one player's sheet, where the ban happens with their
// account and reports in view. The Worker checks admin on every call.

type BugReport = {
  number: number; title: string; url: string; state: string; created_at: string;
  // "" for an issue someone filed on GitHub by hand.
  reporter: string; reporter_name: string; reporter_banned: boolean; reporter_admin: boolean; game_id: string;
};

type Player = {
  user_id: string; display_name: string; created_at: number; admin: boolean;
  banned: boolean; ban_reason: string; banned_by: string; banned_at: number;
};

type ReportState = "open" | "closed" | "all";
type Tab = "reports" | "players";
// What the sheet's foot shows: its action, the ban form, or the unban question.
type SheetMode = "view" | "banning" | "unbanning";

const REPORT_STATES: { state: ReportState; label: string }[] = [
  { state: "open", label: "Open" }, { state: "closed", label: "Closed" }, { state: "all", label: "All" },
];

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const fullDate = (ms: number): string => new Date(ms).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
const shortDate = (ms: number): string => new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
function ago(ms: number): string {
  const minutes = Math.round((ms - Date.now()) / 60000);
  if (Math.abs(minutes) < 60) return relative.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return relative.format(hours, "hour");
  const days = Math.round(hours / 24);
  return Math.abs(days) < 30 ? relative.format(days, "day") : shortDate(ms);
}

const initial = (name: string): string => (name.trim().charAt(0) || "?").toUpperCase();
const issueTitle = (report: BugReport): string => report.title.replace(/^Bug: /, "");
// Below this width the sheet is a drawer over the lists rather than a column beside them.
const narrow = (): boolean => window.matchMedia("(max-width: 860px)").matches;

@customElement("admin-page")
export class AdminPage extends LitElement {
  @property({ attribute: false }) session: Session | null = null;

  @state() private tab: Tab = "reports";
  @state() private forbidden = false;

  @state() private reports: BugReport[] | null = null;
  @state() private reportState: ReportState = "open";
  @state() private reportsError: string | null = null;

  @state() private players: Player[] = [];
  @state() private playersPage = 0;
  @state() private morePlayers = false;
  @state() private playersLoading = false;
  @state() private playersError: string | null = null;
  @state() private search = "";
  @state() private bannedOnly = false;

  // The player whose sheet is open: their id and the name the list showed, then their account row
  // once it's read (null while reading, or for an id no account or ban has).
  @state() private selected: { user_id: string; name: string } | null = null;
  @state() private sheet: Player | null = null;
  @state() private sheetLoading = false;
  @state() private sheetError: string | null = null;
  @state() private mode: SheetMode = "view";
  @state() private banReason = "";
  @state() private saving = false;
  // What the last ban or unban did, shown on the sheet until another player is picked.
  @state() private notice: string | null = null;

  @query(".sheet .close") private closeButton?: HTMLButtonElement;
  @query(".foot textarea") private reasonField?: HTMLTextAreaElement;

  private searchTimer: number | null = null;
  private playersRun = 0;
  private sheetRun = 0;
  // Where focus goes back to when the sheet closes: the row or name that opened it.
  private opener: HTMLElement | null = null;

  static styles = [palette, cursors, css`
    :host {
      display: block; color: var(--text); font-family: var(--font-ui); font-size: 14px; line-height: 1.5;
      /* The tab bar's open-tab orange, here marking the row whose sheet is open. */
      --pick: #e67b3b;
      --card-title: "Onthestack Title", Georgia, serif;
    }
    * { box-sizing: border-box; }
    button, input, textarea { font: inherit; color: inherit; }
    :focus-visible { outline: 2px solid var(--lavender); outline-offset: 2px; }
    a { color: var(--lavender); text-decoration: none; }
    a:hover { text-decoration: underline; }

    header { margin: 4px 0 22px; }
    h1 { font: 700 32px/1.1 var(--card-title); color: var(--text-strong); margin: 0 0 6px; letter-spacing: 0.01em; }
    header p { margin: 0; color: var(--text-dim); max-width: 62ch; }

    .desk { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 28px; align-items: start; }

    /* One bar for both lists: tabs on the left, the open list's filters on the right. */
    .bar { display: flex; align-items: flex-end; justify-content: space-between; gap: 8px 12px; flex-wrap: wrap; border-bottom: 1px solid var(--border); }
    .tabs { display: flex; gap: 4px; }
    .tabs button {
      background: none; border: 0; padding: 8px 12px 10px; font-weight: 600; color: var(--text-dim);
      border-bottom: 2px solid transparent; margin-bottom: -1px;
    }
    .tabs button:hover { color: var(--text-strong); }
    .tabs button[aria-selected="true"] { color: var(--text-strong); border-bottom-color: var(--lavender); }
    .tabs .n { font-weight: 400; color: var(--text-faint); margin-left: 5px; font-variant-numeric: tabular-nums; }
    .filters { display: flex; align-items: center; gap: 12px; padding-bottom: 8px; flex-wrap: wrap; }
    .segments { display: inline-flex; background: var(--input); border: 1px solid var(--border-strong); border-radius: 6px; padding: 2px; }
    .segments button { background: none; border: 0; border-radius: 4px; padding: 3px 10px; font-size: 13px; color: var(--text-dim); }
    .segments button:hover { color: var(--text-strong); }
    .segments button[aria-pressed="true"] { background: var(--surface-3); color: var(--text-strong); }
    input[type="search"] {
      width: 240px; max-width: 100%; padding: 5px 10px; font-size: 13px; border-radius: 6px;
      border: 1px solid var(--border-strong); background: var(--input);
    }
    input[type="search"]::placeholder { color: var(--text-faint); }
    label.check { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: var(--text-dim); }
    input[type="checkbox"] { accent-color: var(--brand-hi); width: 15px; height: 15px; margin: 0; }
    .quiet { background: none; border: 0; padding: 3px 4px; font-size: 13px; color: var(--lavender); }
    .quiet:hover:not(:disabled) { text-decoration: underline; }
    .quiet:disabled { color: var(--text-faint); }

    ol { list-style: none; margin: 0; padding: 0; }
    .row { display: grid; gap: 3px 14px; padding: 12px 12px 12px 16px; border-bottom: 1px solid var(--grid); position: relative; }
    .row.picked { background: var(--surface); }
    .row.picked::before { content: ""; position: absolute; left: 0; top: 9px; bottom: 9px; width: 3px; border-radius: 2px; background: var(--pick); }

    .report { grid-template-columns: minmax(0, 1fr) auto; }
    .report .title { color: var(--text-strong); font-weight: 600; overflow-wrap: anywhere; }
    .report .title .num { color: var(--text-faint); font-weight: 400; font-variant-numeric: tabular-nums; margin-right: 7px; }
    .report.closed .title { color: var(--text-dim); font-weight: 500; }
    .report .when { color: var(--text-faint); font-size: 12.5px; white-space: nowrap; text-align: right; }
    .report .meta { grid-column: 1 / -1; display: flex; flex-wrap: wrap; align-items: center; gap: 4px 16px; font-size: 12.5px; color: var(--text-dim); }
    .who { background: none; border: 0; padding: 0; font-size: 12.5px; font-weight: 600; color: var(--lavender); }
    .who:hover { text-decoration: underline; }
    .tag { display: inline-block; padding: 0 7px; border-radius: 9px; font-size: 11.5px; font-weight: 600; line-height: 18px; margin-left: 4px; }
    .tag.banned { background: var(--bad-soft); color: var(--bad); }
    .tag.admin { background: var(--lavender-soft); color: var(--lavender); }
    .tag.closed { background: var(--surface-3); color: var(--text-dim); margin-left: 0; }

    .player {
      grid-template-columns: 34px minmax(0, 1fr) auto; align-items: center; width: 100%; text-align: left;
      background: none; border: 0; border-bottom: 1px solid var(--grid); border-radius: 0;
    }
    .player:hover { background: var(--surface); }
    .face {
      display: grid; place-items: center; width: 34px; height: 34px; border-radius: 50%;
      background: var(--surface-3); color: var(--text-strong); font: 700 16px/1 var(--card-title);
    }
    .banned > .face { background: var(--bad-soft); color: var(--bad); }
    .player .name { color: var(--text-strong); font-weight: 600; overflow-wrap: anywhere; }
    .player .sub { display: block; color: var(--text-faint); font-size: 12.5px; overflow-wrap: anywhere; }
    .player .joined { color: var(--text-faint); font-size: 12.5px; white-space: nowrap; }

    .note { color: var(--text-dim); padding: 20px 2px; margin: 0; }
    .error { color: var(--bad); margin: 10px 2px; }
    .more { margin: 16px 2px; }

    /* The player sheet: the page's one loud thing, a player read like a card on the table. */
    .sheet { position: sticky; top: 16px; background: var(--surface); border: 1px solid var(--border-strong); border-radius: 10px; }
    .sheet.empty { padding: 20px; color: var(--text-dim); border-style: dashed; background: none; }
    .sheet.empty p { margin: 0; }
    .head { display: grid; grid-template-columns: 52px minmax(0, 1fr) auto; gap: 14px; align-items: center; padding: 18px 16px 14px 18px; }
    .head .face { width: 52px; height: 52px; font-size: 24px; }
    .sheet h2 { font: 700 22px/1.15 var(--card-title); color: var(--text-strong); margin: 0 0 2px; overflow-wrap: anywhere; }
    .head .sub { color: var(--text-faint); font-size: 12px; overflow-wrap: anywhere; }
    .close { align-self: start; background: none; border: 0; padding: 4px 8px; font-size: 20px; line-height: 1; color: var(--text-faint); border-radius: 4px; }
    .close:hover { color: var(--text-strong); background: var(--surface-3); }
    .sheet.banned { border-color: rgb(229 72 77 / 0.5); }
    .ban-band { background: var(--bad-soft); border-block: 1px solid rgb(229 72 77 / 0.3); padding: 10px 18px 12px; }
    .ban-band strong { color: var(--bad); }
    .ban-band .by { color: var(--text-dim); font-size: 12.5px; margin-left: 6px; }
    /* The reason reads like a card's flavour text. */
    .ban-band p { margin: 4px 0 0; font: italic 400 15px/1.4 "Onthestack Text", Georgia, serif; color: var(--text); overflow-wrap: anywhere; }
    dl { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 6px 16px; margin: 0; padding: 14px 18px; font-size: 13px; }
    dt { color: var(--text-faint); }
    dd { margin: 0; overflow-wrap: anywhere; }
    .sheet .note { padding: 10px 18px; }
    .filed { padding: 2px 18px 14px; }
    .filed h3 { font-size: 13px; font-weight: 600; color: var(--text-dim); margin: 0 0 4px; }
    .filed li { padding: 6px 0; border-top: 1px solid var(--grid); font-size: 13px; overflow-wrap: anywhere; }
    .filed .num { color: var(--text-faint); margin-right: 6px; font-variant-numeric: tabular-nums; }
    .none { margin: 0; color: var(--text-faint); font-size: 13px; }
    .foot { padding: 14px 18px 18px; border-top: 1px solid var(--border); display: grid; gap: 10px; margin: 0; }
    .foot p { margin: 0; }
    .foot .error { margin: 0; }
    .done { color: var(--good); }
    .foot label { display: grid; gap: 6px; font-size: 13px; color: var(--text-dim); }
    textarea { width: 100%; resize: vertical; padding: 8px 10px; border-radius: 6px; border: 1px solid var(--border-strong); background: var(--input); }
    .buttons { display: flex; flex-wrap: wrap; gap: 8px; }
    .btn {
      font-size: 13.5px; font-weight: 600; line-height: 1.2; padding: 8px 14px; border-radius: 6px; border: 0;
      background: var(--surface-3); color: var(--text-strong);
    }
    .btn:hover:not(:disabled) { background: #3d3d3d; }
    .btn.ban { background: none; color: var(--bad); box-shadow: inset 0 0 0 1px rgb(229 72 77 / 0.55); }
    .btn.ban:hover:not(:disabled) { background: var(--bad-soft); }
    .btn.confirm { background: var(--bad); color: #fff; }
    .btn.confirm:hover:not(:disabled) { background: #f05a5f; }
    .btn:disabled { opacity: 0.55; cursor: not-allowed; }
    .scrim { display: none; }

    /* A phone or narrow window: the lists take the page and the sheet rises over them from the bottom. */
    @media (max-width: 860px) {
      .desk { grid-template-columns: minmax(0, 1fr); }
      .sheet.empty { display: none; }
      .scrim { display: block; position: fixed; inset: 0; background: rgb(0 0 0 / 0.6); z-index: 20; }
      .sheet {
        position: fixed; left: 0; right: 0; bottom: 0; top: auto; z-index: 21; max-height: 85vh; overflow-y: auto;
        border-radius: 14px 14px 0 0; border-bottom: 0; animation: rise 240ms var(--ease-out);
      }
      .filters { width: 100%; }
      input[type="search"] { flex: 1; width: auto; min-width: 0; }
      .player { grid-template-columns: 34px minmax(0, 1fr); }
      .player .joined { display: none; }
    }
    @keyframes rise { from { transform: translateY(32px); opacity: 0; } }
    @media (prefers-reduced-motion: reduce) { .sheet { animation: none; } }
  `];

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("keydown", this.onKey);
    void this.loadReports();
    void this.loadPlayers(0);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this.onKey);
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer);
  }

  protected updated(changed: PropertyValues<this>): void {
    if (changed.has("mode" as keyof AdminPage) && this.mode === "banning") this.reasonField?.focus();
    // As a drawer the sheet covers the list, so focus moves into it.
    if (changed.has("selected" as keyof AdminPage) && this.selected && narrow()) this.closeButton?.focus();
  }

  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.key !== "Escape" || !this.selected || this.saving) return;
    if (this.mode !== "view") { this.mode = "view"; this.sheetError = null; }
    else this.close();
  };

  private async api(path: string, init: RequestInit = {}): Promise<Response> {
    if (!this.session) throw new Error("Sign in first.");
    const [url, authorized] = authorizedRequest(path, await freshCredential(this.session), init);
    const r = await fetch(url, authorized);
    if (r.status === 403) this.forbidden = true;
    return r;
  }

  private async loadReports(): Promise<void> {
    this.reportsError = null;
    this.reports = null;
    try {
      const r = await this.api(`admin/bug-reports?state=${this.reportState}`);
      if (!r.ok) throw new Error(await r.text());
      this.reports = (await r.json() as { reports: BugReport[] }).reports;
    } catch (e) {
      this.reportsError = message(e);
      this.reports = [];
    }
  }

  // Page 0 starts the list again; a later page adds to it.
  private async loadPlayers(page: number): Promise<void> {
    const run = ++this.playersRun;
    this.playersLoading = true;
    this.playersError = null;
    const params = new URLSearchParams({ q: this.search.trim(), page: String(page) });
    if (this.bannedOnly) params.set("banned", "1");
    try {
      const r = await this.api(`admin/players?${params}`);
      if (!r.ok) throw new Error(await r.text());
      const listed = await r.json() as { players: Player[]; page: number; more: boolean };
      if (run !== this.playersRun) return;
      this.players = page === 0 ? listed.players : [...this.players, ...listed.players];
      this.playersPage = listed.page;
      this.morePlayers = listed.more;
    } catch (e) {
      if (run === this.playersRun) this.playersError = message(e);
    } finally {
      if (run === this.playersRun) this.playersLoading = false;
    }
  }

  // The players search matches an exact user id, so the sheet reads its one account that way.
  private async loadSheet(userId: string): Promise<void> {
    const run = ++this.sheetRun;
    this.sheetLoading = true;
    this.sheetError = null;
    try {
      const r = await this.api(`admin/players?${new URLSearchParams({ q: userId, page: "0" })}`);
      if (!r.ok) throw new Error(await r.text());
      const listed = await r.json() as { players: Player[] };
      if (run === this.sheetRun) this.sheet = listed.players.find((p) => p.user_id === userId) ?? null;
    } catch (e) {
      if (run === this.sheetRun) this.sheetError = message(e);
    } finally {
      if (run === this.sheetRun) this.sheetLoading = false;
    }
  }

  private pick(e: Event, userId: string, name: string): void {
    this.opener = e.currentTarget as HTMLElement;
    if (this.selected?.user_id === userId) return;
    this.selected = { user_id: userId, name };
    this.sheet = null;
    this.mode = "view";
    this.banReason = "";
    this.notice = null;
    void this.loadSheet(userId);
  }

  private close(): void {
    this.selected = null;
    this.mode = "view";
    this.sheetError = null;
    this.opener?.focus();
    this.opener = null;
  }

  private searchChanged(text: string): void {
    this.search = text;
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => void this.loadPlayers(0), 300);
  }

  private async saveBan(method: "PUT" | "DELETE", userId: string, name: string): Promise<void> {
    const reason = this.banReason.trim();
    if (method === "PUT" && reason === "") {
      this.sheetError = "Write why you're banning them, then ban again.";
      this.reasonField?.focus();
      return;
    }
    this.saving = true;
    this.sheetError = null;
    try {
      const body = method === "PUT" ? { user_id: userId, reason } : { user_id: userId };
      const r = await this.api("admin/bans", { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error(await r.text());
      this.mode = "view";
      this.banReason = "";
      this.notice = method === "PUT"
        ? `Banned ${name}. Any game they're in closes within 30 seconds.`
        : `Unbanned ${name}. They can sign in and play again.`;
      await Promise.all([this.loadSheet(userId), this.loadPlayers(0), this.loadReports()]);
    } catch (e) {
      this.sheetError = message(e);
    } finally {
      this.saving = false;
    }
  }

  render(): TemplateResult {
    if (this.forbidden) return html`<header><h1>Admin</h1><p>Only administrators can open this page.</p></header>`;
    return html`
      <header>
        <h1>Admin</h1>
        <p>Read players' bug reports and ban accounts that abuse the game. Pick a player to see their account and reports before you act.</p>
      </header>
      <div class="desk">
        <div>
          <div class="bar">
            <div class="tabs" role="tablist" aria-label="Lists">
              <button role="tab" aria-selected=${this.tab === "reports"} @click=${() => { this.tab = "reports"; }}>Bug reports${this.reports
                ? html`<span class="n">${this.reports.length === 50 ? "50+" : this.reports.length}</span>` : nothing}</button>
              <button role="tab" aria-selected=${this.tab === "players"} @click=${() => { this.tab = "players"; }}>Players</button>
            </div>
            <div class="filters">${this.tab === "reports" ? this.renderReportFilters() : this.renderPlayerFilters()}</div>
          </div>
          <div role="tabpanel">${this.tab === "reports" ? this.renderReports() : this.renderPlayers()}</div>
        </div>
        ${this.renderSheet()}
      </div>
    `;
  }

  private renderReportFilters(): TemplateResult {
    return html`
      <span class="segments" role="group" aria-label="Which bug reports">
        ${REPORT_STATES.map(({ state, label }) => html`<button aria-pressed=${this.reportState === state}
          @click=${() => { if (this.reportState !== state) { this.reportState = state; void this.loadReports(); } }}>${label}</button>`)}
      </span>
      <button class="quiet" ?disabled=${this.reports === null} @click=${() => void this.loadReports()}>Refresh</button>
    `;
  }

  private renderPlayerFilters(): TemplateResult {
    return html`
      <input type="search" placeholder="Search name or user id" aria-label="Search players" .value=${this.search}
        @input=${(e: Event) => this.searchChanged((e.target as HTMLInputElement).value)} />
      <label class="check"><input type="checkbox" .checked=${this.bannedOnly}
        @change=${(e: Event) => { this.bannedOnly = (e.target as HTMLInputElement).checked; void this.loadPlayers(0); }} />Banned only</label>
    `;
  }

  private renderReports(): TemplateResult {
    const reports = this.reports;
    const which = this.reportState === "all" ? "" : `${this.reportState} `;
    return html`
      ${this.reportsError ? html`<p class="error">${this.reportsError}</p>` : nothing}
      ${reports === null ? html`<p class="note">Reading bug reports from GitHub…</p>`
        : reports.length === 0 ? (this.reportsError ? nothing : html`<p class="note">No ${which}bug reports.</p>`)
        : html`<ol>${reports.map((report) => this.renderReport(report))}</ol>`}
    `;
  }

  private renderReport(report: BugReport): TemplateResult {
    const name = report.reporter_name || report.reporter;
    const filed = Date.parse(report.created_at);
    const picked = report.reporter !== "" && this.selected?.user_id === report.reporter;
    return html`<li class="row report ${report.state === "closed" ? "closed" : ""} ${picked ? "picked" : ""}">
      <a class="title" href=${report.url} target="_blank" rel="noopener noreferrer"><span class="num">#${report.number}</span>${issueTitle(report)}</a>
      <span class="when" title=${fullDate(filed)}>${ago(filed)}</span>
      <span class="meta">
        ${report.reporter === "" ? html`<span>Filed on GitHub</span>`
          : html`<span>From <button class="who" @click=${(e: Event) => this.pick(e, report.reporter, name)}>${name}</button>${report.reporter_admin
            ? html`<span class="tag admin">Admin</span>` : nothing}${report.reporter_banned ? html`<span class="tag banned">Banned</span>` : nothing}</span>`}
        ${report.game_id ? html`<span>Game ${report.game_id}</span>` : nothing}
        ${report.state === "closed" ? html`<span class="tag closed">Closed</span>` : nothing}
      </span>
    </li>`;
  }

  private renderPlayers(): TemplateResult {
    const searched = this.search.trim();
    return html`
      ${this.playersError ? html`<p class="error">${this.playersError}</p>` : nothing}
      ${this.players.length === 0
        ? html`<p class="note">${this.playersLoading ? "Reading players…"
          : searched ? `No ${this.bannedOnly ? "banned " : ""}players match “${searched}”. Search by name or a whole user id.`
          : this.bannedOnly ? "Nobody is banned." : "Nobody has signed in yet."}</p>`
        : html`<ol>${this.players.map((player) => this.renderPlayer(player))}</ol>`}
      ${this.morePlayers ? html`<div class="more"><button class="btn" ?disabled=${this.playersLoading}
        @click=${() => void this.loadPlayers(this.playersPage + 1)}>${this.playersLoading ? "Reading…" : "Show 50 more"}</button></div>` : nothing}
    `;
  }

  private renderPlayer(player: Player): TemplateResult {
    // A ban for an account that never signed in has no name.
    const name = player.display_name || player.user_id;
    return html`<li><button class="row player ${player.banned ? "banned" : ""} ${this.selected?.user_id === player.user_id ? "picked" : ""}"
      @click=${(e: Event) => this.pick(e, player.user_id, name)}>
      <span class="face" aria-hidden="true">${initial(name)}</span>
      <span>
        <span class="name">${name}</span>${player.admin ? html`<span class="tag admin">Admin</span>` : nothing}${player.banned
          ? html`<span class="tag banned">Banned</span>` : nothing}
        ${name !== player.user_id ? html`<span class="sub">${player.user_id}</span>` : nothing}
      </span>
      <span class="joined">${player.created_at > 0 ? `Joined ${shortDate(player.created_at)}` : "Never signed in"}</span>
    </button></li>`;
  }

  private renderSheet(): TemplateResult {
    const picked = this.selected;
    if (!picked) return html`<aside class="sheet empty"><p>Pick a player, or the sender of a report, to see their account and ban or unban them.</p></aside>`;
    const account = this.sheet;
    const name = account?.display_name || picked.name;
    const banned = account?.banned === true;
    const theirs = (this.reports ?? []).filter((r) => r.reporter === picked.user_id);
    const which = this.reportState === "all" ? "" : `${this.reportState} `;
    return html`
      <div class="scrim" @click=${() => this.close()}></div>
      <aside class="sheet ${banned ? "banned" : ""}" aria-label=${`Player ${name}`}>
        <div class="head ${banned ? "banned" : ""}">
          <span class="face" aria-hidden="true">${initial(name)}</span>
          <div><h2>${name}</h2><div class="sub">${picked.user_id}</div></div>
          <button class="close" aria-label="Close player" @click=${() => this.close()}>×</button>
        </div>
        ${banned && account ? html`<div class="ban-band">
          <strong>Banned</strong>${account.banned_at > 0 ? html`<span class="by">${shortDate(account.banned_at)} by ${account.banned_by}</span>` : nothing}
          <p>${account.ban_reason}</p>
        </div>` : nothing}
        ${this.sheetLoading && !account ? html`<p class="note">Reading their account…</p>`
          : html`<dl>
            <dt>Joined</dt><dd>${account && account.created_at > 0 ? fullDate(account.created_at) : "Never signed in"}</dd>
            ${account?.admin ? html`<dt>Role</dt><dd>Admin</dd>` : nothing}
          </dl>`}
        <section class="filed">
          <h3>Their ${which}bug reports</h3>
          ${theirs.length === 0 ? html`<p class="none">None in the ${which}list.</p>`
            : html`<ol>${theirs.map((r) => html`<li><a href=${r.url} target="_blank" rel="noopener noreferrer"><span class="num">#${r.number}</span>${issueTitle(r)}</a></li>`)}</ol>`}
        </section>
        ${this.renderSheetFoot(picked.user_id, name, account)}
      </aside>
    `;
  }

  private renderSheetFoot(userId: string, name: string, account: Player | null): TemplateResult {
    const error = this.sheetError ? html`<p class="error" role="alert">${this.sheetError}</p>` : nothing;
    const notice = this.notice ? html`<p class="done" role="status">${this.notice}</p>` : nothing;
    const back = (): void => { this.mode = "view"; this.sheetError = null; };
    if (this.sheetLoading && !account) return html``;
    if (account?.admin) return html`<div class="foot">${notice}<p class="none">Admins can't be banned.</p>${error}</div>`;
    if (this.mode === "banning") {
      return html`<form class="foot" @submit=${(e: Event) => { e.preventDefault(); void this.saveBan("PUT", userId, name); }}>
        <label>Why are you banning ${name}? Only admins see this.
          <textarea rows="3" maxlength="1000" .value=${this.banReason}
            @input=${(e: Event) => { this.banReason = (e.target as HTMLTextAreaElement).value; }}></textarea></label>
        ${error}
        <div class="buttons">
          <button class="btn confirm" type="submit" ?disabled=${this.saving}>${this.saving ? "Banning…" : `Ban ${name}`}</button>
          <button class="btn" type="button" ?disabled=${this.saving} @click=${back}>Cancel</button>
        </div>
      </form>`;
    }
    if (this.mode === "unbanning") {
      return html`<div class="foot">
        <p>Unban ${name}? They can sign in and join games again straight away.</p>
        ${error}
        <div class="buttons">
          <button class="btn confirm" ?disabled=${this.saving} @click=${() => void this.saveBan("DELETE", userId, name)}>${this.saving ? "Unbanning…" : `Unban ${name}`}</button>
          <button class="btn" ?disabled=${this.saving} @click=${back}>Keep the ban</button>
        </div>
      </div>`;
    }
    return html`<div class="foot">
      ${notice}${error}
      <div class="buttons">${account?.banned
        ? html`<button class="btn" @click=${() => { this.mode = "unbanning"; this.notice = null; }}>Unban ${name}</button>`
        : html`<button class="btn ban" @click=${() => { this.mode = "banning"; this.notice = null; }}>Ban ${name}</button>`}</div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { "admin-page": AdminPage }
}
